/**
 * Live coaching during a call (WLA migration, D-219/D-220): the objection library, the script checkpoints and the
 * booking intent of the WLA dialer's `coaching.js` (guide „KassenKompass — PKV Recontact", the guide every migrated
 * WLA brand used). The texts are data, kept verbatim (German, du-Form: they are lines the setter says to the contact).
 * `{vorname}` and `{thema}` are filled by `fillCoachingText`.
 *
 * The matcher is pure: `replayCoaching` folds the call's earlier final segments into a state, `stepCoaching` adds one
 * new segment and returns the hints it triggers. Every hint key fires at most once per call.
 */
export const coachingObjections = [
    {
        key: 'kein_interesse',
        label: 'Kein Interesse',
        patterns: [
            /kein interesse/,
            /brauch(e)? (ich |das )?nicht/,
            /interessiert (mich|uns) nicht/,
            /lass(en sie)? mich in ruhe/,
            /kein bedarf/,
        ],
        responses: [
            'Versteh ich – darf ich trotzdem in einem Satz sagen, worum es ging? Wenn es danach uninteressant ist, schließe ich den Vorgang sofort und du hast Ruhe.',
            'Alles gut. Nur damit ich es richtig zumache: Hat sich das Thema {thema} erledigt – oder ist gerade nur der Zeitpunkt schlecht?',
        ],
    },
    {
        key: 'keine_zeit',
        label: 'Keine Zeit / passt gerade nicht',
        patterns: [
            /keine zeit/,
            /gerade schlecht/,
            /passt (gerade|jetzt) nicht/,
            /bin unterwegs/,
            /im termin/,
            /auf der arbeit/,
            /bin beschäftigt/,
        ],
        responses: [
            'Verstehe – dauert auch keine zwei Minuten. Oder sag mir kurz: Wann erwische ich dich besser – heute Abend oder morgen Vormittag?',
            'Alles klar, dann halte ich es kurz: Eine Frage, dann lasse ich dich in Ruhe weitermachen – okay?',
        ],
    },
    {
        key: 'woher_daten',
        label: 'Woher haben Sie meine Nummer?',
        patterns: [
            /woher (haben|hast|habt) (sie|du|ihr)/,
            /meine nummer/,
            /meine daten/,
            /wie kommen sie an/,
            /wer hat (ihnen|dir) (die|meine)/,
        ],
        responses: [
            'Fair gefragt: Du hast dich bei uns über das Formular zum Thema {thema} eingetragen – daher die Notiz hier. Und genau deshalb rufe ich an: einmal sauber klären, ob das noch aktuell ist.',
            'Gute Frage – das kam von deiner eigenen Anfrage bei KassenKompass. Wenn du willst, schließe ich den Vorgang und du hörst nie wieder was – oder wir klären es in zwei Minuten.',
        ],
    },
    {
        key: 'schon_versorgt',
        label: 'Schon versichert / hat Berater',
        patterns: [
            /schon (gewechselt|versichert|abgeschlossen)/,
            /hab(e)? (schon|bereits) (einen|nen|meinen) (berater|makler|vermittler)/,
            /bin (schon|bereits) (versorgt|versichert)/,
            /schon erledigt/,
            /läuft schon alles/,
        ],
        responses: [
            'Top, dann hat es ja geklappt. Darf ich eine einzige Kontrollfrage stellen, ob da nichts liegengeblieben ist? Danach schließe ich den Vorgang.',
            'Sehr gut – dann nur kurz zum sauberen Abschluss: Bist du beim Abschluss auch zur Beitragsentwicklung im Alter beraten worden? Das ist der Punkt, der am häufigsten fehlt.',
        ],
    },
    {
        key: 'mail_schicken',
        label: 'Schicken Sie mir was per Mail',
        patterns: [
            /per (e-?)?mail/,
            /schick(en sie|t)? mir (was|etwas|unterlagen|infos|informationen)/,
            /mail schicken/,
            /schriftlich/,
        ],
        responses: [
            'Mach ich gern – damit ich dir nicht irgendwas Generisches schicke: zwei kurze Fragen, dann passt die Mail auch wirklich zu dir. Einverstanden?',
            'Kann ich machen – aber ehrlich: Die Mail beantwortet dir nicht, ob sich das für dich lohnt. Die zwei Fragen jetzt schon. Danach entscheidest du.',
        ],
    },
    {
        key: 'erinnert_sich_nicht',
        label: 'Erinnert sich nicht an Anfrage',
        patterns: [
            /erinner/,
            /weiß nichts davon/,
            /nie gemeldet/,
            /nichts eingetragen/,
            /kenn(e)? ich (gar )?nicht/,
            /davon weiß ich nichts/,
        ],
        responses: [
            'Kein Problem, ist auch schon etwas her. Es ging um {thema} – klingelt es damit wieder? Wenn nicht, schließe ich den Vorgang einfach und gut ist.',
            'Alles gut – dann kurz zur Einordnung: Da wurde online eine Anfrage zu {thema} gestellt. Sagt dir das Thema grundsätzlich was, oder soll ich es schließen?',
        ],
    },
    {
        key: 'was_kostet',
        label: 'Was kostet das?',
        patterns: [/was kostet/, /kostet (das|mich|es)/, /was wollt ihr (dafür|verkaufen)/, /preis/],
        responses: [
            'Das Gespräch kostet dich nichts – es geht erstmal nur darum, ob sich ein Termin überhaupt lohnt. Genau das klären die zwei Fragen.',
            'Nichts – ich verkaufe dir am Telefon auch nichts. Ich prüfe nur, ob ein Termin für beide Seiten Sinn ergibt. Fair?',
        ],
    },
    {
        key: 'partner_besprechen',
        label: 'Muss ich mit Partner besprechen',
        patterns: [
            /mit meiner frau/,
            /mit meinem mann/,
            /partner(in)? (be)?sprechen/,
            /zuhause besprechen/,
            /muss ich erst klären/,
        ],
        responses: [
            'Absolut sinnvoll – lass uns den Termin direkt so legen, dass ihr beide dabei seid. Wann passt es euch gemeinsam am besten, eher abends oder am Wochenende?',
        ],
    },
];
/** Script checkpoints, matched on the setter's track; the order is the order of the conversation's phases. */
export const coachingCheckpoints = [
    {
        key: 'namenscheck',
        label: 'Namenscheck (»Spreche ich mit {vorname}?«)',
        patterns: [
            /sprech\w*.{0,14}mit/,
            /spricht.{0,14}mit/,
            /rede ich.{0,10}mit/,
            /bin ich.{0,18}(richtig|verbunden)/,
        ],
    },
    {
        key: 'vorstellung',
        label: 'Vorstellung + KassenKompass-Anker',
        patterns: [/kassen ?kompass/, /klingelt/, /name sagt dir/],
    },
    {
        key: 'permission1',
        label: 'Erlaubnisfrage 1 (»darf ich direkt sagen worum es geht?«)',
        patterns: [/erlaubt.{0,15}direkt/, /darf ich direkt/, /worum es (geht|gehen)/, /direkt zu sagen/],
    },
    {
        key: 'grund',
        label: 'Anrufgrund (Notiz + Thema + Termin-Idee)',
        patterns: [/notiz/, /gemeldet ha(st|ben)/, /kontakt auf(zu)?nehmen/, /beide seiten/, /vor einiger zeit/],
    },
    {
        key: 'permission2',
        label: 'Erlaubnisfrage 2 (»ein, zwei Fragen – gestattet?«)',
        patterns: [
            /(ein,? )?zwei (kurze )?fragen/,
            /gegenseitig/,
            /vorgang.{0,20}schließ/,
            /gestattet/,
            /erlaubt\??$/,
        ],
    },
];
const bookingPatterns = [
    /termin/i,
    /kalender/i,
    /wann (passt|w[uü]rde|h[aä]tten|k[oö]nnten)/i,
    /(vormittag|nachmittag|abend)s? (oder|besser|lieber)/i,
    /(montag|dienstag|mittwoch|donnerstag|freitag)\b.{0,24}(oder|um|geht|passt)/i,
    /passt (ihnen|dir|euch|mir|uns)/i,
    /n[aä]chste woche/i,
    /(15|30|45) minuten.{0,20}(zeit|sprechen|gespr[aä]ch)/i,
];
export const BOOKING_INTENT_HINT_KEY = 'booking_intent';
export const BOOKING_INTENT_TITLE = 'Terminwunsch erkannt';
export const BOOKING_INTENT_TEXT = 'Das Gespräch geht Richtung Termin.';
/** Rolling context windows of the WLA matcher (characters). */
const CHECKPOINT_WINDOW = 800;
const BOOKING_WINDOW = 400;
export const emptyCoachingState = { fired: [], checkpointBuffer: '', bookingBuffer: '' };
function objectionKey(key) {
    return `objection:${key}`;
}
function checkpointKey(key) {
    return `checkpoint:${key}`;
}
/** The hint key as it is pushed to the browser (`objection:<id>`, `checkpoint:<id>`, `booking_intent`). */
export function coachingHintKey(kind, key) {
    if (kind === 'objection')
        return objectionKey(key);
    if (kind === 'checkpoint')
        return checkpointKey(key);
    return BOOKING_INTENT_HINT_KEY;
}
/** Adds one final segment: objections and the booking intent on the contact's track, checkpoints and the booking intent on the setter's. */
export function stepCoaching(state, segment) {
    const text = segment.text.toLowerCase();
    const fired = new Set(state.fired);
    const hints = [];
    let checkpointBuffer = state.checkpointBuffer;
    const bookingBuffer = `${state.bookingBuffer} ${text}`.slice(-BOOKING_WINDOW);
    if (segment.speaker === 'contact') {
        const objection = coachingObjections.find((candidate) => !fired.has(objectionKey(candidate.key)) && candidate.patterns.some((pattern) => pattern.test(text)));
        if (objection !== undefined) {
            fired.add(objectionKey(objection.key));
            hints.push({
                kind: 'objection',
                key: objectionKey(objection.key),
                title: objection.label,
                text: objection.responses.join('\n\n'),
            });
        }
    }
    else {
        checkpointBuffer = `${checkpointBuffer} ${text}`.slice(-CHECKPOINT_WINDOW);
        for (const checkpoint of coachingCheckpoints) {
            if (fired.has(checkpointKey(checkpoint.key)))
                continue;
            if (checkpoint.patterns.some((pattern) => pattern.test(checkpointBuffer))) {
                fired.add(checkpointKey(checkpoint.key));
                hints.push({
                    kind: 'checkpoint',
                    key: checkpointKey(checkpoint.key),
                    title: checkpoint.label,
                    text: '',
                });
            }
        }
    }
    if (!fired.has(BOOKING_INTENT_HINT_KEY) && bookingPatterns.some((pattern) => pattern.test(bookingBuffer))) {
        fired.add(BOOKING_INTENT_HINT_KEY);
        hints.push({
            kind: 'booking_intent',
            key: BOOKING_INTENT_HINT_KEY,
            title: BOOKING_INTENT_TITLE,
            text: BOOKING_INTENT_TEXT,
        });
    }
    return { state: { fired: [...fired], checkpointBuffer, bookingBuffer }, hints };
}
/** Folds the earlier segments of a call (in order) into the matcher state. */
export function replayCoaching(segments) {
    return segments.reduce((state, segment) => stepCoaching(state, segment).state, emptyCoachingState);
}
/** Replaces `{vorname}` and `{thema}` in a coaching text. */
export function fillCoachingText(text, values) {
    return text.replaceAll('{vorname}', values.firstName).replaceAll('{thema}', values.topic);
}
export function fillCoachingHint(hint, values) {
    return { ...hint, title: fillCoachingText(hint.title, values), text: fillCoachingText(hint.text, values) };
}
//# sourceMappingURL=coaching.js.map